import { useRef, useEffect } from 'react'

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

interface MonthYearPickerProps {
  startYear: number
  endMonth: Date
  currentMonth: Date
  onSelect: (month: Date) => void
  onClose: () => void
}

export function MonthYearPicker({
  startYear,
  endMonth,
  currentMonth,
  onSelect,
  onClose,
}: MonthYearPickerProps) {
  const ref = useRef<HTMLDivElement>(null)

  const currentYearRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  // Auto-scroll to current month's year on open
  useEffect(() => {
    currentYearRef.current?.scrollIntoView({ block: 'center' })
  }, [])

  const endYear = endMonth.getFullYear()
  const endMonthIdx = endMonth.getMonth()
  const currentYear = currentMonth.getFullYear()
  const currentMonthIdx = currentMonth.getMonth()

  const years: number[] = []
  for (let y = startYear; y <= endYear; y++) {
    years.push(y)
  }

  const handleSelect = (year: number, monthIdx: number) => {
    onSelect(new Date(year, monthIdx, 1))
    onClose()
  }

  return (
    <div
      ref={ref}
      className="absolute inset-0 z-10 overflow-y-auto bg-popover rounded-md border border-border shadow-md"
    >
      <div className="p-3">
        {years.map((year) => (
          <div key={year} ref={year === currentYear ? currentYearRef : undefined} className="mb-3">
            <div className="text-xs font-semibold text-foreground mb-1.5">{year}</div>
            <div className="grid grid-cols-4 gap-1">
              {MONTHS.map((label, idx) => {
                const isFuture = year > endYear || (year === endYear && idx > endMonthIdx)
                const isPast = year < startYear
                const isCurrent = year === currentYear && idx === currentMonthIdx

                return (
                  <button
                    key={label}
                    disabled={isFuture || isPast}
                    onClick={() => handleSelect(year, idx)}
                    className={`rounded px-2 py-1 text-[11px] transition-colors ${
                      isCurrent
                        ? 'bg-primary text-primary-foreground font-medium'
                        : isFuture || isPast
                          ? 'text-muted-foreground/40 cursor-not-allowed'
                          : 'text-foreground hover:bg-hover'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
